import React from 'react';
import { navigate } from 'gatsby';
import { FormattedMessage } from 'react-intl';
import Layout from '@4c/layout';
import { css } from 'css-literal-loader/styled';

import paths from '../paths';
import Form from './Form';
import FormCheck from './FormCheck';
import configureIndividual from '../schema/configureIndividual';
import withReservation from '../utils/Reservation';
import {
  SingleUserPriceFootnote,
  ButterflyIqLightningFootnote,
  ButterflyIqUsbcFootnote,
} from './PriceFootnotes';
import ProbeImageSegment from './ProbeImageSegment';
import SubscriptionInfoLink from './SubscriptionInfoLink';
import WarrantyInfoLink from './WarrantyInfoLink';
import CompatibilityInfoLink from './CompatibilityInfoLink';
import Responsive from './Responsive';
import Text from './Text';

const propTypes = {};
const {
  Button,
  DeviceLightningName,
  DeviceUsbcName,
  Heading,
  Price,
  Section,
  MadeForIPhoneIPadBadge,
} = ProbeImageSegment;

const styles = require('./ConfigureSoloPageSegment-styles.module.scss');

const SubmitButton = React.forwardRef((props, ref) => {
  return (
    <Form.SubmitButton as={Button} ref={ref}>
      <FormattedMessage
        id="configure.solo.cloud.submit"
        defaultMessage="Continue"
      />
    </Form.SubmitButton>
  );
});

// eslint-disable-next-line react/no-multi-comp
class ConfigureSoloPageSegment extends React.Component {
  state = {};

  handleChange = deviceType => {
    this.setState(({ errors }) => {
      return {
        value: {
          deviceType,
        },
        errors: deviceType ? {} : errors,
      };
    });
  };

  handleSubmit = async value => {
    const { cart } = this.props;
    const { deviceType } = value;

    await cart.setQuantities(
      1,
      deviceType === 'lightning' ? 1 : 0,
      deviceType === 'usbc' ? 1 : 0,
    );

    navigate(paths.cart);
  };

  handleErrors = errors => {
    this.setState({ errors });
  };

  render() {
    const { cart } = this.props;
    const {
      cloudIndividualPricePerMonth,
      devicePrice,
    } = cart.remote.inventory;
    const { value, errors } = this.state;

    return (
      <Responsive smDown>
        {isMobile => (
          <ProbeImageSegment
            title={
              <FormattedMessage
                id="configure.solo.breadCrumb"
                defaultMessage="Configure"
              />
            }
            showImage={!isMobile}
          >
            {!isMobile && (
              <span className={styles.madeForIphone}>
                <MadeForIPhoneIPadBadge />
              </span>
            )}

            <Heading
              wrap
              display="flex"
              justify="space-between"
              align="center"
            >
              <FormattedMessage
                id="configure.solo.cloud.heading"
                defaultMessage="Individual Subscription"
              />
              {!isMobile && <SubscriptionInfoLink />}
            </Heading>

            <Text level={isMobile ? '3' : '2'} alignLeft marginBottom>
              <FormattedMessage
                id="configure.solo.cloud"
                defaultMessage="Scan and store with unlimited software upgrades and HIPAA-compliant cloud storage."
              />
            </Text>
            <Section
              pad={4}
              display="flex"
              align={isMobile ? 'stretch' : 'flex-start'}
              direction={isMobile ? 'column' : 'row'}
            >
              {isMobile && (
                <Layout
                  direction="row"
                  justify="space-between"
                  align="flex-end"
                >
                  <SubscriptionInfoLink />
                  <MadeForIPhoneIPadBadge />
                </Layout>
              )}

              <Section>
                <Text level="1" alignLeft>
                  <Price
                    price={cloudIndividualPricePerMonth * 12}
                    perYear
                    noCents
                  />
                </Text>
                <div>
                  <SingleUserPriceFootnote />
                </div>
              </Section>
            </Section>
            <Heading
              wrap
              bordered
              display="flex"
              justify="space-between"
              align="center"
            >
              <FormattedMessage
                id="configure.solo.device.heading"
                defaultMessage="Butterfly iQ"
              />
              <WarrantyInfoLink />
            </Heading>

            <Text level={isMobile ? '3' : '2'} alignLeft>
              <FormattedMessage
                id="configure.solo.device"
                defaultMessage="Choose the iQ for your iPhone or iPad."
              />
            </Text>
            <div>
              <CompatibilityInfoLink
                compatibilitySegmentRef={this.props.compatibilitySegmentRef}
              />
            </div>

            <Form
              schema={configureIndividual}
              key="step-2"
              onSubmit={this.handleSubmit}
              value={value}
              errors={errors}
              onError={this.handleErrors}
              className={styles.form}
            >
              <Layout direction="row">
                {!isMobile && (
                  <div className={styles.mobilePrice}>
                    <Text level="1">
                      <Price price={devicePrice} noCents />
                    </Text>
                  </div>
                )}
                <FormCheck
                  className={styles.formCheck}
                  type="radio"
                  checked={value && value.deviceType === 'lightning'}
                  onChange={() => this.handleChange('lightning')}
                >
                  <Layout
                    direction="column"
                    grow
                    className={styles.formOption}
                  >
                    <Layout direction="row" justify="space-between">
                      <Text level={isMobile ? '2' : '1'}>
                        <DeviceLightningName />
                      </Text>
                      {isMobile && (
                        <Text level="2">
                          <Price price={devicePrice} noCents />
                        </Text>
                      )}
                    </Layout>
                    <ButterflyIqLightningFootnote alignLeft />
                  </Layout>
                </FormCheck>
              </Layout>
              <Layout direction="row">
                {!isMobile && (
                  <div className={styles.mobilePrice}>
                    <Text level="1">
                      <Price price={devicePrice} noCents />
                    </Text>
                  </div>
                )}
                <FormCheck
                  className={styles.formCheck}
                  type="radio"
                  checked={value && value.deviceType === 'usbc'}
                  onChange={() => this.handleChange('usbc')}
                >
                  <Layout
                    direction="column"
                    grow
                    className={styles.formOption}
                  >
                    <Layout direction="row" justify="space-between">
                      <Text level={isMobile ? '2' : '1'}>
                        <DeviceUsbcName />
                      </Text>
                      {isMobile && (
                        <Text level="2">
                          <Price price={devicePrice} noCents />
                        </Text>
                      )}
                    </Layout>
                    <ButterflyIqUsbcFootnote alignLeft />
                  </Layout>
                </FormCheck>
              </Layout>
              <Form.Field
                name="deviceType"
                withTooltip
                events={null}
                as={SubmitButton}
              />
            </Form>
          </ProbeImageSegment>
        )}
      </Responsive>
    );
  }
}

ConfigureSoloPageSegment.propTypes = propTypes;

export default withReservation(ConfigureSoloPageSegment);
